import { supabase } from '../lib/supabase';

const tenantGuard = async () => {
  const impersonationData = sessionStorage?.getItem('superAdminImpersonating');
  if (impersonationData) {
    try {
      const parsed = JSON.parse(impersonationData);
      if (parsed?.targetId) return parsed.targetId;
    } catch {
      // Fall back to the authenticated user when the stored value is invalid.
    }
  }

  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return user.id;
};

const toCamelCase = (value) => {
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(toCamelCase);

  return Object.entries(value).reduce((result, [key, item]) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = typeof item === 'object' ? toCamelCase(item) : item;
    return result;
  }, {});
};

const toSnakeCase = (value) => Object.entries(value).reduce((result, [key, item]) => {
  result[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] = item;
  return result;
}, {});

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const normalizeRecipients = (recipients) => [...new Set(
  (Array.isArray(recipients) ? recipients : [])
    .map((email) => String(email).trim().toLowerCase())
    .filter(Boolean)
)];

export const submissionSettingsService = {
  async getSettings(formId) {
    const userId = await tenantGuard();
    const { data: form, error: formError } = await supabase
      .from('forms')
      .select('id, settings')
      .eq('id', formId)
      .eq('user_id', userId)
      .single();

    if (formError || !form) throw new Error('Form not found or access denied');

    const { data, error } = await supabase
      .from('form_submission_settings')
      .select('*')
      .eq('form_id', formId)
      .maybeSingle();

    if (error) throw error;
    const savedSettings = data ? toCamelCase(data) : {};
    return {
      ...savedSettings,
      turnstileEnabled: savedSettings?.turnstileEnabled ?? form?.settings?.turnstileEnabled ?? false,
    };
  },

  async saveSettings(formId, settings) {
    const userId = await tenantGuard();
    const { data: form, error: formError } = await supabase
      .from('forms')
      .select('id, settings')
      .eq('id', formId)
      .eq('user_id', userId)
      .single();

    if (formError || !form) throw new Error('Form not found or access denied');

    const { id, formId: ignoredFormId, createdAt, updatedAt, ...editableSettings } = settings;
    editableSettings.emailNotificationRecipients = normalizeRecipients(editableSettings.emailNotificationRecipients);
    if (editableSettings.emailNotificationRecipients.length > 20) {
      throw new Error('Add no more than 20 notification recipients.');
    }
    if (editableSettings.emailNotificationRecipients.some((email) => !EMAIL_PATTERN.test(email))) {
      throw new Error('Remove invalid recipient email addresses before saving.');
    }
    if (editableSettings.emailNotificationsEnabled && editableSettings.emailNotificationRecipients.length === 0) {
      throw new Error('Add at least one recipient before enabling email notifications.');
    }
    const payload = {
      ...toSnakeCase(editableSettings),
      form_id: formId,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('form_submission_settings')
      .upsert(payload, { onConflict: 'form_id' })
      .select()
      .single();

    if (error) throw error;

    // Public forms can read only their form row. Mirror this one safe boolean
    // there so the renderer and Edge Functions know whether to show/require
    // Turnstile without exposing the creator-only submission settings table.
    const { error: formUpdateError } = await supabase
      .from('forms')
      .update({
        settings: {
          ...(form?.settings || {}),
          turnstileEnabled: editableSettings?.turnstileEnabled === true,
        },
        updated_at: new Date().toISOString(),
      })
      .eq('id', formId)
      .eq('user_id', userId);

    if (formUpdateError) throw formUpdateError;
    return toCamelCase(data);
  },
};